/**
 * Basisrekenen (REK) Support Registry
 *
 * Support content (hints, explanations, visuals) for REK families.
 *
 * Module: REK (Basisrekenen)
 * Families in taxonomy: 17
 * Families with support: 17 (100% coverage)
 *
 * Created: 2026-03-25
 * Updated: 2026-03-25 (Step 6 - Added 11 missing families)
 * Status: ✅ COMPLETE - All families have support
 */

import { FamilySupportEntry } from '../familySupportRegistry'

/**
 * REK Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const REK_SUPPORT: Record<string, FamilySupportEntry> = {
  // Skill: tafels (3 families)
  'REK-TAFELS-BASIS': {
    familyId: 'REK-TAFELS-BASIS',
    defaultExplanation:
      'Tafels 1-10: ken de uitkomsten uit je hoofd. Automatisering is de basis voor al het verdere rekenen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Lastige tafels',
        content:
          'Moeilijkste tafelsommen:\n7 × 8 = 56 (truc: 5-6-7-8)\n6 × 7 = 42\n6 × 8 = 48\n8 × 9 = 72\n\nTafel van 9 truc: cijfersom is altijd 9\n9×2=18 (1+8=9), 9×3=27 (2+7=9)',
      },
    ],
  },
  'REK-TAFELS-TOEPASSEN': {
    familyId: 'REK-TAFELS-TOEPASSEN',
    defaultExplanation:
      'Tafels toepassen in context: herken welke tafel je nodig hebt. Bijvoorbeeld: 6 zakken met 8 appels = 6 × 8 = 48 appels.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Herkennen',
        content:
          'Zoek sleutelwoorden:\n"per" → vermenigvuldigen\n"zakken van" → vermenigvuldigen\n"verdelen over" → delen\n\nVoorbeeld: 5 dozen met 7 pennen per doos\n→ 5 × 7 = 35 pennen',
      },
    ],
  },
  'REK-TAFELS-UITBREIDEN': {
    familyId: 'REK-TAFELS-UITBREIDEN',
    defaultExplanation:
      'Tafels uitbreiden: gebruik tafels die je kent om grotere tafels te maken. 12 × 7 = (10 × 7) + (2 × 7) = 70 + 14 = 84.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategieën',
        content:
          'Splitsen: 12×7 = (10×7)+(2×7) = 70+14 = 84\nVerdubbelen: 16×4 = 8×8 = 64\nHalveren: 6×14 = 12×7 = 84\n\nDeeltafels: 48÷6 = ? → denk 6×8=48',
      },
    ],
  },

  // Skill: cijferend (4 families)
  'REK-CIJF-PLUS': {
    familyId: 'REK-CIJF-PLUS',
    defaultExplanation:
      'Cijferend optellen: zet de getallen onder elkaar, begin rechts bij de eenheden. Als de som groter is dan 9, schrijf je het laatste cijfer en onthoud je het eerste.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stap voor stap',
        content:
          'Voorbeeld: 347 + 285\n1. Eenheden: 7+5=12 → schrijf 2, onthoud 1\n2. Tientallen: 4+8+1=13 → schrijf 3, onthoud 1\n3. Honderdtallen: 3+2+1=6\nAntwoord: 632',
      },
    ],
  },
  'REK-CIJF-MIN': {
    familyId: 'REK-CIJF-MIN',
    defaultExplanation:
      'Cijferend aftrekken: zet de getallen onder elkaar, begin rechts. Als het bovenste cijfer kleiner is dan het onderste, leen je 10 van de volgende kolom.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Lenen',
        content:
          'Voorbeeld: 742 − 368\n1. E: 2−8 kan niet → leen: 12−8=4\n2. T: 3−6 kan niet → leen: 13−6=7\n3. H: 6−3=3\nAntwoord: 374\n\nBij een 0 (bijv. 503−247): leen door naar de volgende kolom.',
      },
    ],
  },
  'REK-CIJF-KEER': {
    familyId: 'REK-CIJF-KEER',
    defaultExplanation:
      'Cijferend vermenigvuldigen: vermenigvuldig elk cijfer van het onderste getal met het bovenste getal. Tel alle tussenuitkomsten op.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stap voor stap',
        content:
          'Voorbeeld: 47 × 6\n1. 6×7=42 → schrijf 2, onthoud 4\n2. 6×4=24, +4=28 → schrijf 28\nAntwoord: 282\n\nBij twee cijfers (bijv. 47×23):\nBereken 47×3 en 47×20, tel op.',
      },
    ],
  },
  'REK-CIJF-DEEL': {
    familyId: 'REK-CIJF-DEEL',
    defaultExplanation:
      'Cijferend delen (staartdeling): verdeel het getal cijfer voor cijfer. Hoeveel keer past de deler in het getal? Schrijf de rest op en neem het volgende cijfer erbij.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Staartdeling',
        content:
          'Voorbeeld: 156 ÷ 6\n1. 6 in 15? → 2 keer (2×6=12), rest 3\n2. Neem 6 erbij → 36\n3. 6 in 36? → 6 keer (6×6=36), rest 0\nAntwoord: 26\n\nTip: schat eerst (156÷6 ≈ 150÷6 = 25)',
      },
    ],
  },

  // Skill: handig (3 families)
  'REK-HANDIG-PLUS-MIN': {
    familyId: 'REK-HANDIG-PLUS-MIN',
    defaultExplanation:
      'Handig optellen en aftrekken: kies de slimste strategie. Splitsen, compenseren of rijgen — afhankelijk van de getallen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Drie strategieën',
        content:
          'Splitsen: 48+37 → 48+30=78, 78+7=85\nCompenseren: 99+56 → 100+56=156, 156−1=155\nRijgen: 63−28 → 63−30=33, 33+2=35\n\nKies compenseren als een getal dicht bij een tiental/honderdtal ligt.',
      },
    ],
  },
  'REK-HANDIG-KEER': {
    familyId: 'REK-HANDIG-KEER',
    defaultExplanation:
      'Handig vermenigvuldigen: gebruik verdubbelen, halveren of factoren splitsen. 16 × 25 = 8 × 50 = 4 × 100 = 400.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategieën',
        content:
          'Verdubbelen/halveren:\n16×25 → 8×50 → 4×100 = 400\n\nFactoren splitsen:\n24×15 = 24×10 + 24×5 = 240+120 = 360\n\nHandige getallen:\n×5 = ×10÷2\n×25 = ×100÷4',
      },
    ],
  },
  'REK-HANDIG-DEEL': {
    familyId: 'REK-HANDIG-DEEL',
    defaultExplanation:
      'Handig delen: gebruik herhaald aftrekken of factoren. 144 ÷ 12 = 144 ÷ 4 ÷ 3 = 36 ÷ 3 = 12.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategieën',
        content:
          'Factoren splitsen:\n144÷12 → 144÷4÷3 → 36÷3 = 12\n\nHerhaald aftrekken:\n85÷5 → 85−50=35 (10×), 35−35=0 (7×)\n→ 10+7 = 17\n\nHandige getallen:\n÷5 = ×2÷10\n÷25 = ×4÷100',
      },
    ],
  },

  // Skill: schatten (1 family)
  'REK-SCHATTEN': {
    familyId: 'REK-SCHATTEN',
    defaultExplanation:
      'Schatten: rond af naar handige getallen voordat je rekent. Controleer of je antwoord in de buurt van de schatting ligt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie',
        content:
          'Rond af naar handige getallen:\n298 × 5 ≈ 300 × 5 = 1.500\n\nControleer: als je antwoord ver van 1.500 ligt, heb je waarschijnlijk een fout gemaakt.',
      },
    ],
  },

  // Skill: volgorde (1 family)
  'REK-VOLGORDE': {
    familyId: 'REK-VOLGORDE',
    defaultExplanation:
      'Volgorde van bewerkingen: haakjes eerst, dan machten, dan × en ÷ (van links naar rechts), dan + en − (van links naar rechts).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Meneer Van Dansen Wansen',
        content:
          'M = Machten\nV = Vermenigvuldigen\nD = Delen\nW = Wortels\nA = Aftrekken\nS = Sommen (optellen)\n\nHaakjes gaan ALTIJD eerst!',
      },
    ],
  },

  // Skill: context (3 families)
  'REK-CONTEXT-PLUS-MIN': {
    familyId: 'REK-CONTEXT-PLUS-MIN',
    defaultExplanation:
      'Redactiesommen met optellen en aftrekken: lees de vraag goed, bepaal welke bewerking je nodig hebt, en reken uit.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappen',
        content:
          '1. Wat wordt er gevraagd?\n2. Welke getallen heb je nodig?\n3. Optellen of aftrekken?\n4. Reken uit en controleer\n\nSignaalwoorden:\n"samen", "totaal" → optellen\n"over", "verschil" → aftrekken',
      },
    ],
  },
  'REK-CONTEXT-KEER-DEEL': {
    familyId: 'REK-CONTEXT-KEER-DEEL',
    defaultExplanation:
      'Redactiesommen met vermenigvuldigen en delen: herken of je moet vermenigvuldigen (groepen van) of delen (verdelen over).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Herkennen',
        content:
          'Vermenigvuldigen:\n"per", "elke", "zakken van"\n→ aantal groepen × aantal per groep\n\nDelen:\n"verdelen over", "per persoon"\n→ totaal ÷ aantal groepen\n\nControleer: past het antwoord bij de vraag?',
      },
    ],
  },
  'REK-CONTEXT-MEERSTAPS': {
    familyId: 'REK-CONTEXT-MEERSTAPS',
    defaultExplanation:
      'Meerstaps-opgaven: je moet meerdere bewerkingen uitvoeren. Maak een plan: welke stap eerst, welke daarna?',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Aanpak',
        content:
          '1. Lees de vraag zorgvuldig\n2. Welke informatie heb je nodig?\n3. Maak een stappenplan:\n   - Stap 1: bereken...\n   - Stap 2: gebruik uitkomst voor...\n4. Controleer: klopt het antwoord?\n\nTip: schrijf tussenstappen op!',
      },
    ],
  },

  // Skill: gemengd (2 families)
  'REK-GEMENGD-BASIS': {
    familyId: 'REK-GEMENGD-BASIS',
    defaultExplanation:
      'Gemengde bewerkingen: kies de beste strategie per som. Soms cijferend, soms handig, soms schatten.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Strategie kiezen',
        content:
          'Kijk naar de getallen:\n- Dicht bij 10/100? → compenseren\n- Makkelijke tafels? → uit het hoofd\n- Grote getallen? → cijferend\n- Ruw antwoord? → schatten\n\nOefen met verschillende strategieën!',
      },
    ],
  },
  'REK-GEMENGD-TOETS': {
    familyId: 'REK-GEMENGD-TOETS',
    defaultExplanation:
      'Toets-achtige mix: alle bewerkingen en contextopgaven door elkaar. Lees goed, kies de juiste strategie, en werk nauwkeurig.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Toets-tips',
        content:
          '1. Lees de vraag twee keer\n2. Onderstreep belangrijke getallen\n3. Bepaal de bewerking\n4. Schat eerst (controle)\n5. Reken nauwkeurig uit\n6. Controleer je antwoord\n\nTijd over? Check je werk!',
      },
    ],
  },
}

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const REK_SUPPORT_STATS = {
  moduleCode: 'REK',
  moduleName: 'Basisrekenen',
  totalFamilies: Object.keys(REK_SUPPORT).length,
  totalFamiliesInTaxonomy: 17,
  coveragePercentage: Math.round((Object.keys(REK_SUPPORT).length / 17) * 100),
  missingFamilies: 0,
  withHints: Object.values(REK_SUPPORT).filter((e) => e.hints?.length).length,
  withVisuals: Object.values(REK_SUPPORT).filter((e) => e.defaultVisual).length,
  withExplanations: Object.values(REK_SUPPORT).filter(
    (e) => e.defaultExplanation,
  ).length,
  requiresHintsCount: Object.values(REK_SUPPORT).filter((e) => e.requiresHints)
    .length,
  requiresVisualCount: Object.values(REK_SUPPORT).filter(
    (e) => e.requiresVisual,
  ).length,
}
